import { ZButton } from "@/shared/components/button";
import FileUpload from "@/shared/components/custom/file-upload";
import { ZInput } from "@/shared/components/input";
import {
  ZModal,
  ZModalBody,
  ZModalContent,
  ZModalFooter,
  ZModalHeader,
} from "@/shared/components/modal";
import { UploadedFile } from "@/shared/types/file-upload";
import { ZTooltip } from "@/shared/components/ZTooltip/ZTooltip";

import { useLoggedInMember } from "@/modules/auth/states";
import { TeamRole } from "@/modules/team/types";
import { addNotification } from "@/shared/states/notification";
import { cn, Radio, RadioGroup, Select, SelectItem, Switch, Textarea, Tooltip } from "@nextui-org/react";
import { AxiosError } from "axios";
import { cloneDeep } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { simplifyFileTypes } from "../../../../shared/components/custom/file-upload/index";
import {
  AGENT_TYPES,
  GapAssessmentTypes,
  RiskAssessmentTypes,
  UploadDialogState,
} from "../../types/index.ts";
import { helper } from "./helper.ts";
import { checkAlphaNumeric, containsSpecialCharacters, containsSpecialCharactersForTitle, processDataBasedOnAssessment, getCharacterLimit } from "./util.ts";
import { ROUTES } from "@/shared/constants/routes.ts";
import { InfoIcon } from "lucide-react";
import { EMAIL_REGEX } from "../../../../shared/constants/index.ts";

// Add filetypes from simplifyFileTypes keys.
const DEFAULT_ALLOWED_FILE_TYPES = simplifyFileTypes([]).map(
  (key: string) => key
);

// Define a type for the error messages
type ErrorMessages = {
  session?: string;
  [key: string]: string | undefined; // allows for dynamic keys
};

export default function UploadDialog({
  data,
  setIsOpen,
}: {
  data: UploadDialogState;
  setIsOpen: (data: UploadDialogState) => void;
}) {
  const { isOpen, type } = data;
  const loggedInMember = useLoggedInMember();

  const helperData = useMemo(() => {
    if(type === AGENT_TYPES.RISK_ASSESSMENT){
      const temp = cloneDeep((helper as any)[type as AGENT_TYPES])

      if(temp.dropdown && loggedInMember){
        temp.dropdown.options = temp.dropdown.options.filter((option: any) => {
          if(option.value === 'nist_ai_rmf' && loggedInMember.roles.find(rol => rol.role_id === TeamRole.ZANIA_NIST_AI_RMF)){
            return true
          }
          if(option.value === 'nist-csf-2' && loggedInMember.roles.find(rol => rol.role_id === TeamRole.ZANIA_RISK_ASSESSMENT)){
            return true
          }
          // saurabh update value to your values and role to your value
          if(option.value === RiskAssessmentTypes.FAIR && loggedInMember.roles.find(rol => rol.role_id === TeamRole.ZANIA_FAIR_ASSESSMENT)){
            return true
          }

          return false
        })
      }

      return temp
    }

    return (helper as any)[type as AGENT_TYPES]

  }, [type, loggedInMember]);
  const [isLoading, setIsLoading] = useState(false);
  const [allFiles, setAllFiles] = useState<UploadedFile[]>([]);
  const [errors, setErrors] = useState<ErrorMessages>({});
  const [selectedDropdownOption, setSelectedDropdownOption] = useState<
    string
  >();
  const [selectedDropdown2Option, setSelectedDropdown2Option] = useState<
    string
  >();
  const [selectedComplianceFrameworks, setSelectedComplianceFrameworks] = useState<
    string[]
  >([]);
  const [isToggleSelected, setIsToggleSelected] = useState(false);
  const fileUploadRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [inputs, setInputs] = useState<{ [key: string]: string | boolean | string[] }>({});

  // Compliance frameworks options
  const complianceFrameworks = [
    { value: "basel_ii_iii", label: "Basel II/III" },
    { value: "ccpa", label: "CCPA" },
    { value: "cis_controls", label: "CIS Controls" },
    { value: "cobit", label: "COBIT" },
    { value: "coso", label: "COSO" },
    { value: "csa_ccm", label: "CSA CCM" },
    { value: "csa_star", label: "CSA STAR" },
    { value: "enisa", label: "ENISA" },
    { value: "eu_ai_act", label: "EU AI Act" },
    { value: "fair", label: "FAIR" },
    { value: "fisma", label: "FISMA" },
    { value: "fedramp", label: "FedRAMP" },
    { value: "gdpr", label: "GDPR" },
    { value: "glba", label: "GLBA" },
    { value: "hipaa", label: "HIPAA" },
    { value: "hitrust_csf", label: "HITRUST CSF" },
    { value: "isa_iec_62443", label: "ISA/IEC 62443" },
    { value: "iso_iec_27001", label: "ISO/IEC 27001" },
    { value: "iso_iec_27002", label: "ISO/IEC 27002" },
    { value: "iso_iec_27005", label: "ISO/IEC 27005" },
    { value: "iso_iec_27017", label: "ISO/IEC 27017" },
    { value: "iso_iec_27018", label: "ISO/IEC 27018" },
    { value: "iso_iec_27701", label: "ISO/IEC 27701" },
    { value: "iso_iec_38500", label: "ISO/IEC 38500" },
    { value: "iso_iec_42001", label: "ISO/IEC 42001" },
    { value: "itil", label: "ITIL" },
    { value: "nist_ai_rmf", label: "NIST AI RMF" },
    { value: "nist_csf_2", label: "NIST CSF 2.0" },
    { value: "nist_sp_800_171", label: "NIST SP 800-171" },
    { value: "nist_sp_800_30", label: "NIST SP 800-30" },
    { value: "nist_sp_800_37", label: "NIST SP 800-37" },
    { value: "nist_sp_800_53", label: "NIST SP 800-53" },
    { value: "oecd_ai_principles", label: "OECD AI Principles" },
    { value: "owasp_asvs", label: "OWASP ASVS" },
    { value: "owasp_samm", label: "OWASP SAMM" },
    { value: "owasp_top_10", label: "OWASP Top 10" },
    { value: "pci_dss", label: "PCI DSS" },
    { value: "soc_1", label: "SOC 1" },
    { value: "soc_2_type_1", label: "SOC 2 Type I" },
    { value: "soc_2_type_2", label: "SOC 2 Type II" },
    { value: "sox", label: "SOX" },
    { value: "swift_csp", label: "SWIFT CSP" }
  ];

  // Dynamic labels and placeholders based on documentation type
  const getDynamicLabelAndPlaceholder = (docType: string) => {
    const typeMap: { [key: string]: { label: string; placeholder: string } } = {
      'Policy': { label: 'Policy Name', placeholder: 'Add policy name' },
      'Standard': { label: 'Standard Name', placeholder: 'Add standard name' },
      'Guideline': { label: 'Guideline Name', placeholder: 'Add guideline name' },
      'Procedure': { label: 'Procedure Name', placeholder: 'Add procedure name' }
    };
    return typeMap[docType] || { label: 'Policy Name', placeholder: 'Add policy name' };
  };

  // Get the logged-in member to check permissions
  const member = useLoggedInMember();
  // Check if user has access to FAIR assessment
  const userRoles = member?.roles?.map(({ role_id }) => role_id as TeamRole) || [];
  const hasFairAccess = userRoles.includes(TeamRole.ZANIA_FAIR_ASSESSMENT);

  // Filter dropdown options based on permissions
  const filteredDropdownOptions = useMemo(() => {
    if (!helperData?.dropdown?.options) return [];

    return (helperData.dropdown.options as { value: string; label: string; isDefault: boolean }[]).filter(option => {
      // If it's the FAIR option, only show it if the user has access
      if (option.value === RiskAssessmentTypes.FAIR) {
        return hasFairAccess;
      }
      // Show all other options
      return true;
    });
  }, [helperData?.dropdown?.options, hasFairAccess]);

  const navigate = useNavigate();

  // Use effect to set contact email from logged in user when requestVendor is toggled on
  useEffect(() => {
    if (inputs.requestVendor === true && member?.email_address && (!inputs.contactEmail || inputs.contactEmail === '')) {
      setInputs(prevInputs => ({
        ...prevInputs,
        contactEmail: member.email_address
      }));
    }
  }, [inputs.requestVendor, member]);

  // Combining the framework change and modal open effects into one
  useEffect(() => {
    if (isOpen) {
      // When modal opens, reset everything except session title
      setIsLoading(false);
      setErrors({});
      setSelectedComplianceFrameworks([]); // Reset compliance frameworks
      setInputs(prevInputs => {
        const newInputs = { ...prevInputs };
        // Only preserve the session title
        const sessionTitle = newInputs.session;
        
        // Set the contact email if the member has an email
        if (member?.email_address) {
          newInputs.contactEmail = member.email_address;
        }
        
        return { session: sessionTitle, contactEmail: member?.email_address || '' };
      });
    }
  }, [isOpen, member]);

  // Separate effect for framework changes
  useEffect(() => {
    if (selectedDropdownOption) {
      setIsToggleSelected(false);
      setAllFiles([]);
      // Reset all inputs except session title
      setInputs(prevInputs => ({
        ...prevInputs,
        risk_statement: '', // Clear risk statement when framework changes
      }));
    }
  }, [selectedDropdownOption]);

  // Set default framework option
  useEffect(() => {
    let defaultOption = helperData?.dropdown?.options.find(
      (option: { isDefault: boolean }) => option.isDefault
    );
    if (!defaultOption && helperData?.dropdown?.options?.[0]) {
      defaultOption = helperData.dropdown.options[0];
    }

    if (
      (type !== AGENT_TYPES.GAP_ASSESSMENT)
    ) {
      setSelectedDropdownOption(defaultOption?.value ?? "");
    }
    
    // Set default option for dropdown2 if present
    if (helperData?.dropdown2) {
      let defaultOption2 = helperData.dropdown2.options.find(
        (option: { isDefault: boolean }) => option.isDefault
      );
      if (!defaultOption2 && helperData.dropdown2.options[0]) {
        defaultOption2 = helperData.dropdown2.options[0];
      }
      setSelectedDropdown2Option(defaultOption2?.value ?? "");
    }
  }, [helperData?.dropdown?.option, helperData?.dropdown2?.options, type, isOpen]);

  const isValidEmail = (email: string) => {
    return EMAIL_REGEX.test(email);
  };
  // Add this useEffect to set the default radio value when the dropdown changes
  useEffect(() => {
    if (selectedDropdownOption === RiskAssessmentTypes.NIST_CSF_2 && helperData?.radio) {
      // Find the default option or use the first option if no default is specified
      const defaultRadioOption = helperData.radio.options.find(
        (opt: { value: string; label: string; isDefault?: boolean }) => opt.isDefault
      )?.value || helperData.radio.options[0]?.value;

      setInputs(prevInputs => ({
        ...prevInputs,
        [helperData.radio.key]: defaultRadioOption
      }));
    }
  }, [selectedDropdownOption, helperData]);

  const isCustomizeNistCsf2 =
      selectedDropdownOption === RiskAssessmentTypes.NIST_CSF_2 &&
      inputs[helperData?.radio?.key] === "customize";

  const buttonDisabled = useMemo(() => {
    // Check for any validation errors
    const hasValidationErrors = Object.values(errors).some(error => error !== "" && error !== undefined);
    if (hasValidationErrors) {
      // If there are validation errors, disable the button
      return true;
    }
  
    const areInputsMissing = helperData?.inputs?.some(
      (input: { key: string; isRequired: boolean }) =>
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        input.isRequired &&
        ((inputs[input.key] ?? "") as string).trim().length === 0
    );

    // Check if vendor email is required when requestVendor is true
    if (inputs.requestVendor === true && (!inputs.vendorEmail || (inputs.vendorEmail as string).trim().length === 0)) {
      return true;
    }
    // Check if contact email is required and valid when requestVendor is true
    if (
      inputs.requestVendor === true &&
      (
        !inputs.contactEmail ||
        (inputs.contactEmail as string).trim().length === 0 ||
        !isValidEmail(inputs.contactEmail as string)
      )
    ) {
      return true;
    }

    if(inputs.requestVendor && (inputs.contactEmail as string)?.trim() === (inputs.vendorEmail as string)?.trim()){
      setErrors({ vendorEmail: "Vendor email can not be same as contact email" });
    }

    if (areInputsMissing) {
      // Some required inputs are missing.
      return true;
    }

    // Check if compliance frameworks are required for policy management but missing
    if (type === AGENT_TYPES.POLICY_MANAGEMENT && 
        (!selectedComplianceFrameworks || selectedComplianceFrameworks.length === 0)) {
      return true;
    }

    // Check if policy upload is required for update_existing but missing
    if (type === AGENT_TYPES.POLICY_MANAGEMENT && selectedDropdown2Option === "update_existing") {
      const policyFiles = allFiles.filter(file => file.type === "policy");
      if (policyFiles.length === 0) {
        return true;
      }
    }

    // Check if risk field is required for FAIR but missing
    if (selectedDropdownOption === RiskAssessmentTypes.FAIR &&
      (!inputs.risk_statement || (inputs.risk_statement as string).trim().length === 0)) {
      return true;
    }

    if (!selectedDropdownOption && type === AGENT_TYPES.GAP_ASSESSMENT) {
      return true;
    }

    // Check if any required upload fields are missing files
    let maxFilesExceeded = false;
    for (const upload of helperData?.uploads ?? []) {
      const filteredAllFiles = allFiles.filter(
        (file) => file.type === upload.type
      );

      // Check if required upload is missing files
      if (upload.isRequired && !filteredAllFiles.length && selectedDropdownOption !== RiskAssessmentTypes.FAIR && !isCustomizeNistCsf2) {
        // Required upload field has no files
        return true;
      }

      const AllowedMaxFiles = selectedDropdownOption === RiskAssessmentTypes.NIST_CSF_2 ? 400 : upload.maxFiles

      maxFilesExceeded =
        maxFilesExceeded || filteredAllFiles.length > AllowedMaxFiles;
    }

    if (maxFilesExceeded) {
      // the number of files exceeds the maximum allowed uploads as defined in the helperData.uploads array
      return true;
    }

    if (!type) {
      // the type is not defined; impossible to proceed
      return true;
    }

    return allFiles.some((file) => file.status !== "success");
  }, [allFiles, type, helperData, inputs, selectedDropdownOption, errors, selectedComplianceFrameworks]);

  const handleSubmit = async () => {
    setIsLoading(true);

    let isError = false;
    for (const input of helperData?.inputs ?? []) {
      if (input?.isRequired && !inputs[input.key]) {
        isError = true;
        setErrors((prevErrors) => ({
          ...prevErrors,
          [input.key]: "This field is required",
        }));
      }

      // character limit for input - use dynamic limit based on agent type
      const characterLimit = getCharacterLimit(type, input.key);
      if(inputs[input.key] && input.type === 'text' && inputs[input.key].toString().length > characterLimit){
        isError = true;
        setErrors((prevErrors) => ({
          ...prevErrors,
          [input.key]: `Input cannot exceed ${characterLimit} characters`,
        }));
      }
      if (input.key === "title") {
        if (containsSpecialCharactersForTitle(inputs[input.key] as string)) {
          isError = true;
          setErrors((prevErrors) => ({
            ...prevErrors,
            [input.key]: `Input cannot include special characters except '-', '_', or '/'.`
          }));
        }
      } else if (input.key === "vendorName") {
        if (checkAlphaNumeric(inputs[input.key] as string)) {
          isError = true;
          setErrors((prevErrors) => ({
            ...prevErrors,
            [input.key]: "Special characters are not allowed.",
          }));
        }
      } else if (inputs[input.key] && typeof inputs[input.key] === 'string' && containsSpecialCharacters(inputs[input.key] as string)) {
        isError = true;
        setErrors((prevErrors) => ({
          ...prevErrors,
          [input.key]: "Special characters are not allowed.",
        }));
      }
    }

    // Skip vendor email and contact email validation when requestVendor is false
    if (inputs.requestVendor !== true) {
      // Clear any existing errors for vendor-related fields
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors.vendorEmail;
        delete newErrors.contactEmail;
        return newErrors;
      });
    }

    if (isError) {
      setIsLoading(false);
      return;
    }

    try {
      // Check if any required uploads are missing
      const hasRequiredUploads = helperData?.uploads?.some((upload: { isRequired: boolean }) => upload.isRequired);
      if (hasRequiredUploads && allFiles.length === 0 && selectedDropdownOption !== RiskAssessmentTypes.FAIR && !isCustomizeNistCsf2) {
        return;
      }
      
      // Call processDataBasedOnAssessment and capture any returned session ID
      const sessionId = await processDataBasedOnAssessment(
        type,
        allFiles,
        navigate,
        inputs,
        selectedDropdownOption,
        isToggleSelected
      );
      
    } catch (error) {
      const errorType = (error as AxiosError<{ error_type: string }>).response
        ?.data.error_type;

      if (errorType === "Validation Error") {
        setErrors({ title: "Session name already exists" });
        return;
      }
      console.error(error);
      addNotification({
        message: "Error processing the uploaded file(s)",
        type: "error",
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };
  const handleSetFiles = (newFiles: UploadedFile[], fileType: string) => {
    setAllFiles((prevFiles) => {
      const otherTypeFiles = prevFiles.filter((file) => file.type !== fileType);
      const combinedFiles = [...otherTypeFiles, ...newFiles];
      const uniqueFiles = combinedFiles.reduce((acc, current) => {
        const existingFile = acc.find(
          (item) => item.file.name === current.file.name && item.type === current.type
        );
        if (!existingFile) {
          return [...acc, current];
        } else {
          return acc.map((item) =>
            item.file.name === current.file.name ? current : item
          );
        }
      }, [] as UploadedFile[]);

      // If questionnaire, update questionnaire_url in inputs as string (first signedUrl)
      if (fileType === "questionnaire") {
        setInputs((prevInputs) => ({
          ...prevInputs,
          questionnaire_url: newFiles
            .filter(f => f.signedUrl)
            .map(f => f.signedUrl as string)[0] ?? "",
        }));
      }

      // Scroll to the newly added file
      setTimeout(() => {
        const lastFile = newFiles[newFiles.length - 1];
        if (lastFile && fileUploadRefs.current[fileType]) {
          fileUploadRefs.current[fileType]?.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          });
        }
      }, 100);

      return uniqueFiles;
    });
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    key: string,
    type: string
  ) => {
    const newValue = e.target.value.trimStart();

    // Clear existing error for this field
    setErrors({ ...errors, [key]: "" });

    // Skip validation if field is empty (except for required fields, which are validated on submit)
    if (!newValue) {
      // Update inputs state with empty value
      setInputs({
        ...inputs,
        [key]: newValue,
      });
      return;
    }

    // Validate input based on field type and key
    let validationError = "";

    // Email validation for vendorEmail and contactEmail fields
    if ((key === "vendorEmail" || key === "contactEmail") && !isValidEmail(newValue)) {
      validationError = "Please enter a valid email address";
    }
    // Character limit validation - use dynamic limit based on agent type
    else if (type === 'text' && key !== "vendorEmail" && key !== "contactEmail") {
      const characterLimit = getCharacterLimit(data.type, key);
      if (newValue.length > characterLimit) {
        validationError = `Input cannot exceed ${characterLimit} characters`;
      }
    }
    // Special character validation based on field type
    else if (key === "title") {
      if(containsSpecialCharactersForTitle(newValue))
        validationError = "Input cannot include special characters except '-', '_', or '/'";
    }
    else if (key === "vendorName") {
      if(checkAlphaNumeric(newValue))
        validationError = "Special characters are not allowed";
    }
    else if (newValue && containsSpecialCharacters(newValue) && key !== "vendorEmail" && key !== "contactEmail") {
      validationError = "Special characters are not allowed";
    }

    // Update error state if validation failed
    if (validationError) {
      setErrors(prev => ({ ...prev, [key]: validationError }));
    }

    // Update inputs state regardless of validation result
    setInputs({
      ...inputs,
      [key]: newValue,
    });
  };

  return (
    <ZModal
      isOpen={isOpen}
      onOpenChange={() =>
        setIsOpen({
          isOpen: false,
        })
      }
      aria-label="Upload Dialog"
      className="max-h-[calc(100vh-96px)] overflow-y-auto"
    >
      <ZModalContent className="max-w-lg">
        <ZModalHeader className="flex text-lg flex-col w-full items-center pb-4">
          <p>{helperData?.title}</p>
          <p className="text-sm text-center font-normal text-[#777780]">
            {helperData?.desc}
          </p>
        </ZModalHeader>
        <ZModalBody className="pt-0 overflow-auto">
          <div className="grid gap-4 py-0">
            <div className="grid w-full items-center gap-4">
              {/* Display Session Title first */}
              {type === AGENT_TYPES.POLICY_MANAGEMENT && helperData?.inputs?.[0] ? (
                <ZInput
                  key={helperData.inputs[0].key}
                  label={helperData.inputs[0].label}
                  placeholder={helperData.inputs[0].placeholder}
                  type={helperData.inputs[0].type}
                  value={
                    inputs[helperData.inputs[0].key] &&
                      typeof inputs[helperData.inputs[0].key] === "string"
                      ? (inputs[helperData.inputs[0].key] as string)
                      : ""
                  }
                  onChange={(e) => handleInputChange(e, helperData.inputs[0].key, helperData.inputs[0].type)}
                  labelPlacement="outside"
                  maxLength={25}
                  errorMessage={errors[helperData.inputs[0].key]}
                  isInvalid={!!errors[helperData.inputs[0].key]}
                  variant="faded"
                  autoFocus={true}
                  className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm shadow-none rounded-md"
                  classNames={{
                    inputWrapper: cn(
                      "w-full shadow-none rounded-md border-1 border-gray-300",
                      !!errors[helperData.inputs[0].key] && "border-[#F31260]"
                    ),
                  }}
                />
              ) : null}
              
              {/* Side-by-side dropdowns for policy management */}
              {type === AGENT_TYPES.POLICY_MANAGEMENT && helperData?.dropdown && helperData?.dropdown2 ? (
                <div className="flex gap-4 w-full">
                  <div className="flex-1">
                    <div className="flex items-center mb-1">
                      <span className="text-sm">{helperData.dropdown.label}</span>
                      <Tooltip 
                        showArrow 
                        placement="bottom"
                        offset={10}
                        classNames={{
                          content: "p-3 w-[240px] bg-black opacity-90 text-white rounded-md",
                          arrow: "bg-black",
                          base: "z-[9999]"
                        }}
                        content={
                          <div className="text-sm">
                            <p className="text-xs mb-8">What type of documentation are you working with?</p>
                            
                            <ul className="list-disc pl-5 space-y-2 text-xs">
                              <li >Policy (High-level intent)</li>
                              <li>Standard (Specific requirements to meet control objectives)</li>
                              <li>Guideline (Recommended but non-mandatory guidance)</li>
                              <li>Procedure (Step-by-step implementation instructions)</li>
                            </ul>
                          </div>
                        }
                      >
                        <span><InfoIcon className="h-4 w-4 ml-1 text-gray-500 cursor-help" /></span>
                      </Tooltip>
                    </div>
                    <Select
                      classNames={{
                        mainWrapper: cn(
                          "w-full shadow-none rounded-md border-1 border-gray-300",
                          !!errors.session && "border-[#F31260]"
                        ),
                        popoverContent: cn(
                          "w-full shadow-none rounded-md border-1 border-gray-300",
                          !!errors.session && "border-[#F31260]"
                        ),
                      }}
                      items={filteredDropdownOptions}
                      labelPlacement="outside"
                      value={selectedDropdownOption}
                      placeholder={helperData.dropdown.placeholder}
                      onChange={(value) => {
                        setSelectedDropdownOption(value.target.value);
                      }}
                      selectedKeys={selectedDropdownOption ? new Set([selectedDropdownOption]) : new Set()}
                      disabledKeys={selectedDropdownOption ? new Set([selectedDropdownOption]) : new Set()}
                    >
                      {(item: { value: string; label: string }) => (
                        <SelectItem key={item.value} value={item.value}>
                          {item.label}
                        </SelectItem>
                      )}
                    </Select>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center mb-1">
                      <span className="text-sm">{helperData.dropdown2.label}</span>
                      <Tooltip 
                        showArrow 
                        placement="bottom"
                        offset={10}
                        classNames={{
                          content: "p-3 w-[240px] bg-black opacity-90 text-white rounded-md",
                          arrow: "bg-black",
                          base: "z-[9999]"
                        }}
                        content={
                          <div className="text-xs">
                            <p className="mb-6">What do you want to do with the selected documentation type?</p>
                            <ul className="list-disc pl-5 space-y-2 text-xs">
                              <li>Create from scratch</li>
                              <li>Update an existing item</li>
                              <li>Derive from another type <span className="text-xs ">(e.g., derive a procedure from policy)</span></li>
                            </ul>
                          </div>
                        }
                      >
                        <span><InfoIcon className="h-4 w-4 ml-1 text-gray-500 cursor-help" /></span>
                      </Tooltip>
                    </div>
                    <Select
                      classNames={{
                        mainWrapper: cn(
                          "w-full shadow-none rounded-md border-1 border-gray-300",
                          !!errors.session && "border-[#F31260]"
                        ),
                        popoverContent: cn(
                          "w-full shadow-none rounded-md border-1 border-gray-300",
                          !!errors.session && "border-[#F31260]"
                        ),
                      }}
                      labelPlacement="outside"
                      value={selectedDropdown2Option}
                      placeholder={helperData.dropdown2.placeholder}
                      onChange={(value) => {
                        setSelectedDropdown2Option(value.target.value);
                        setInputs({
                          ...inputs,
                          [helperData.dropdown2.key]: value.target.value,
                        });
                        // Clear ALL files when switching between actions
                        setAllFiles([]);
                      }}
                      selectedKeys={selectedDropdown2Option ? new Set([selectedDropdown2Option]) : new Set()}
                      disabledKeys={selectedDropdown2Option ? new Set([selectedDropdown2Option]) : new Set()}
                    >
                      {helperData.dropdown2.options.map((item: { value: string; label: string }) => (
                        <SelectItem key={item.value} value={item.value}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </Select>
                  </div>
                </div>
              ) : null}
              
              {/* Display Policy Name and Context fields */}
              {type === AGENT_TYPES.POLICY_MANAGEMENT && helperData?.inputs ? 
                helperData.inputs.slice(1).map((input: {
                  key: string;
                  label: string;
                  placeholder: string;
                  type: string;
                  isRequired: boolean;
                }, index: number) => {
                  // Show Upload Document field when "Update" is selected
                  if (input.key === "policyName" && selectedDropdown2Option === "update_existing") {
                    const dynamicContent = getDynamicLabelAndPlaceholder(selectedDropdownOption || 'policy');
                    return (
                      <div key={input.key}>
                        {/* Policy Name Text Input - Show first */}
                        <ZInput
                          label={dynamicContent.label}
                          placeholder={dynamicContent.placeholder}
                          type={input.type}
                          value={
                            inputs[input.key] &&
                              typeof inputs[input.key] === "string"
                              ? (inputs[input.key] as string)
                              : ""
                          }
                          onChange={(e) => handleInputChange(e, input.key, input.type)}
                          labelPlacement="outside"
                          maxLength={25}
                          errorMessage={errors[input.key]}
                          isInvalid={!!errors[input.key]}
                          variant="faded"
                          className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm shadow-none rounded-md"
                          classNames={{
                            inputWrapper: cn(
                              "w-full shadow-none rounded-md border-1 border-gray-300",
                              !!errors[input.key] && "border-[#F31260]"
                            ),
                          }}
                        />

                        {/* Upload Document to Update - Show second */}
                        <div className="grid w-full items-center gap-1.5 mt-4">
                          <p className="text-sm">Upload Document to Update</p>
                          <FileUpload
                            key={`policy-${selectedDropdown2Option}`}
                            maxFiles={1}
                            isMultiple={false}
                            allowedFileTypes={[
                              "application/pdf",
                              "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                              "application/msword"
                            ]}
                            setFiles={(files: UploadedFile[], type: string) =>
                              handleSetFiles(files, "policy")
                            }
                            type="policy"
                            ref={(el: HTMLDivElement) => {
                              fileUploadRefs.current.policy = el;
                            }}
                          />
                        </div>
                        
                        {/* Compliance Frameworks Dropdown - Always show */}
                        <div className="grid w-full items-center gap-1.5 mt-4">
                          <p className="text-sm">Compliance Frameworks</p>
                          <Select
                            classNames={{
                              mainWrapper: cn(
                                "w-full shadow-none rounded-md border-1 border-gray-300"
                              ),
                              popoverContent: cn(
                                "w-full shadow-none rounded-md border-1 border-gray-300"
                              ),
                            }}
                            labelPlacement="outside"
                            placeholder="Select compliance frameworks"
                            selectionMode="multiple"
                            onSelectionChange={(keys) => {
                              const selectedArray = Array.from(keys as Set<string>);
                              setSelectedComplianceFrameworks(selectedArray);
                              setInputs({
                                ...inputs,
                                complianceFrameworks: selectedArray,
                              });
                            }}
                            selectedKeys={new Set(selectedComplianceFrameworks)}
                            renderValue={(items) => {
                              if (items.length === 0) return "";
                              if (items.length === 1) return items[0].textValue;
                              
                              // Create a temporary element to measure text width
                              const measureText = (text: string) => {
                                const canvas = document.createElement('canvas');
                                const context = canvas.getContext('2d');
                                if (context) {
                                  context.font = '14px system-ui, -apple-system, sans-serif';
                                  return context.measureText(text).width;
                                }
                                return text.length * 8; // Fallback estimate
                              };
                              
                              const maxWidth = 280; // Approximate available width in the select
                              
                              // If all items fit, return them all
                              const allText = items.map(item => item.textValue).join(", ");
                              if (measureText(allText) <= maxWidth) {
                                return allText;
                              }
                              
                              // Otherwise, find how many we can fit with "+ X others"
                              let displayText = items[0].textValue;
                              let displayCount = 1;
                              
                              // Try to fit as many frameworks as possible while leaving room for "+ X others"
                              for (let i = 1; i < items.length - 1; i++) {
                                const remaining = items.length - (i + 1);
                                const testText = displayText + ", " + items[i].textValue;
                                const withOthers = `${testText} + ${remaining} others`;
                                
                                if (measureText(withOthers) <= maxWidth) {
                                  displayText = testText;
                                  displayCount++;
                                } else {
                                  break;
                                }
                              }
                              
                              const remaining = items.length - displayCount;
                              return (
                                <span>
                                  {displayText} <span style={{ color: '#006FEE' }}>+ {remaining} others</span>
                                </span>
                              );
                            }}
                          >
                            {complianceFrameworks.map((framework) => (
                              <SelectItem key={framework.value} value={framework.value}>
                                {framework.label}
                              </SelectItem>
                            ))}
                          </Select>
                        </div>
                      </div>
                    );
                  }
                  
                  // For Policy Name field when NOT updating, add the new components after it
                  if (input.key === "policyName" && selectedDropdown2Option !== "update_existing") {
                    const dynamicContent = getDynamicLabelAndPlaceholder(selectedDropdownOption || 'policy');
                    return (
                      <div key={input.key}>
                        {/* Dynamic Name Input based on Documentation Type */}
                        <ZInput
                          label={dynamicContent.label}
                          placeholder={dynamicContent.placeholder}
                          type={input.type}
                          value={
                            inputs[input.key] &&
                              typeof inputs[input.key] === "string"
                              ? (inputs[input.key] as string)
                              : ""
                          }
                          onChange={(e) => handleInputChange(e, input.key, input.type)}
                          labelPlacement="outside"
                          maxLength={25}
                          errorMessage={errors[input.key]}
                          isInvalid={!!errors[input.key]}
                          variant="faded"
                          className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm shadow-none rounded-md"
                          classNames={{
                            inputWrapper: cn(
                              "w-full shadow-none rounded-md border-1 border-gray-300",
                              !!errors[input.key] && "border-[#F31260]"
                            ),
                          }}
                        />
                        
                        {/* Derive from Document - File Upload (Optional) - Only for Create/Derive */}
                        <div className="grid w-full items-center gap-1.5 mt-4">
                          <p className="text-sm">Derive from Documents <span className="">(Optional)</span></p>
                          <FileUpload
                            key={`derive-document-${selectedDropdown2Option}`}
                            maxFiles={5}
                            isMultiple={true}
                            allowedFileTypes={[
                              "application/pdf",
                              "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                              "application/msword"
                            ]}
                            setFiles={(files: UploadedFile[], type: string) =>
                              handleSetFiles(files, "derive-document")
                            }
                            type="derive-document"
                            ref={(el: HTMLDivElement) => {
                              fileUploadRefs.current["derive-document"] = el;
                            }}
                          />
                        </div>
                        
                        {/* Compliance Frameworks Dropdown */}
                        <div className="grid w-full items-center gap-1.5 mt-4">
                          <p className="text-sm">Compliance Frameworks</p>
                          <Select
                            classNames={{
                              mainWrapper: cn(
                                "w-full shadow-none rounded-md border-1 border-gray-300"
                              ),
                              popoverContent: cn(
                                "w-full shadow-none rounded-md border-1 border-gray-300"
                              ),
                            }}
                            labelPlacement="outside"
                            placeholder="Select compliance frameworks"
                            selectionMode="multiple"
                            onSelectionChange={(keys) => {
                              const selectedArray = Array.from(keys as Set<string>);
                              setSelectedComplianceFrameworks(selectedArray);
                              setInputs({
                                ...inputs,
                                complianceFrameworks: selectedArray,
                              });
                            }}
                            selectedKeys={new Set(selectedComplianceFrameworks)}
                            renderValue={(items) => {
                              if (items.length === 0) return "";
                              if (items.length === 1) return items[0].textValue;
                              
                              // Create a temporary element to measure text width
                              const measureText = (text: string) => {
                                const canvas = document.createElement('canvas');
                                const context = canvas.getContext('2d');
                                if (context) {
                                  context.font = '14px system-ui, -apple-system, sans-serif';
                                  return context.measureText(text).width;
                                }
                                return text.length * 8; // Fallback estimate
                              };
                              
                              const maxWidth = 280; // Approximate available width in the select
                              
                              // If all items fit, return them all
                              const allText = items.map(item => item.textValue).join(", ");
                              if (measureText(allText) <= maxWidth) {
                                return allText;
                              }
                              
                              // Otherwise, find how many we can fit with "+ X others"
                              let displayText = items[0].textValue;
                              let displayCount = 1;
                              
                              // Try to fit as many frameworks as possible while leaving room for "+ X others"
                              for (let i = 1; i < items.length - 1; i++) {
                                const remaining = items.length - (i + 1);
                                const testText = displayText + ", " + items[i].textValue;
                                const withOthers = `${testText} + ${remaining} others`;
                                
                                if (measureText(withOthers) <= maxWidth) {
                                  displayText = testText;
                                  displayCount++;
                                } else {
                                  break;
                                }
                              }
                              
                              const remaining = items.length - displayCount;
                              return (
                                <span>
                                  {displayText} <span style={{ color: '#006FEE' }}>+ {remaining} others</span>
                                </span>
                              );
                            }}
                          >
                            {complianceFrameworks.map((framework) => (
                              <SelectItem key={framework.value} value={framework.value}>
                                {framework.label}
                              </SelectItem>
                            ))}
                          </Select>
                        </div>
                      </div>
                    );
                  }
                  
                  return input.type === "textarea" ? (
                    <div key={input.key}>
                      <div className="flex items-center mb-1">
                        <span className="text-sm">{input.label}</span>
                      </div>
                      <Textarea
                        placeholder={input.placeholder}
                        value={
                          inputs[input.key] &&
                            typeof inputs[input.key] === "string"
                            ? (inputs[input.key] as string)
                            : ""
                        }
                        onChange={(e) => {
                          setErrors({ ...errors, [input.key]: "" });
                          setInputs({
                            ...inputs,
                            [input.key]: e.target.value.trimStart(),
                          });
                        }}
                        labelPlacement="outside"
                        errorMessage={errors[input.key]}
                        isInvalid={!!errors[input.key]}
                        variant="faded"
                        classNames={{
                          inputWrapper: cn(
                            "w-full shadow-none rounded-md border-1 border-gray-300",
                            !!errors[input.key] && "border-[#F31260]"
                          ),
                        }}
                      />
                    </div>
                  ) : (
                    <ZInput
                      key={input.key}
                      label={input.label}
                      placeholder={input.placeholder}
                      type={input.type}
                      value={
                        inputs[input.key] &&
                          typeof inputs[input.key] === "string"
                          ? (inputs[input.key] as string)
                          : ""
                      }
                      onChange={(e) => handleInputChange(e, input.key, input.type)}
                      labelPlacement="outside"
                      maxLength={25}
                      errorMessage={errors[input.key]}
                      isInvalid={!!errors[input.key]}
                      variant="faded"
                      className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm shadow-none rounded-md"
                      classNames={{
                        inputWrapper: cn(
                          "w-full shadow-none rounded-md border-1 border-gray-300",
                          !!errors[input.key] && "border-[#F31260]"
                        ),
                      }}
                    />
                  );
                })
              : null}

              {/* Display inputs for non-policy management types */}
              {type !== AGENT_TYPES.POLICY_MANAGEMENT && helperData?.inputs?.map(
                (input: {
                  key: string;
                  label: string;
                  placeholder: string;
                  type: string;
                  isRequired: boolean;
                }) => {
                  if (input.type === "textarea") {
                    return (
                      <Textarea
                        key={input.key}
                        label={input.label}
                        placeholder={input.placeholder}
                        value={
                          inputs[input.key] &&
                            typeof inputs[input.key] === "string"
                            ? (inputs[input.key] as string)
                            : ""
                        }
                        onChange={(e) => {
                          setErrors({ ...errors, [input.key]: "" });
                          setInputs({
                            ...inputs,
                            [input.key]: e.target.value.trimStart(),
                          });
                        }}
                        labelPlacement="outside"
                        errorMessage={errors[input.key]}
                        isInvalid={!!errors[input.key]}
                        variant="faded"
                        classNames={{
                          inputWrapper: cn(
                            "w-full shadow-none rounded-md border-1 border-gray-300",
                            !!errors[input.key] && "border-[#F31260]"
                          ),
                        }}
                      />
                    );
                  }
                  
                  if (input.type === "toggle") {
                    return (
                      <div className="" key={input.key}>
                        <div className="flex items-center">
                          <Switch
                            isSelected={
                              inputs[input.key] &&
                                typeof inputs[input.key] === "boolean"
                                ? (inputs[input.key] as boolean)
                                : false
                            }
                            onValueChange={(isSelected) => {
                              if (input.key === "requestVendor") {
                                // Clear vendor-related fields when toggle is turned off
                                if (!isSelected) {
                                  setInputs({
                                    ...inputs,
                                    [input.key]: isSelected,
                                    vendorEmail: "",
                                    contactEmail: "",
                                    questionnaire_url: "",
                                  });
                                  // Clear questionnaire files
                                  setAllFiles((prevFiles) => 
                                    prevFiles.filter(file => file.type !== "questionnaire")
                                  );
                                  // Clear any validation errors for these fields
                                  setErrors((prevErrors) => {
                                    const newErrors = { ...prevErrors };
                                    delete newErrors.vendorEmail;
                                    delete newErrors.contactEmail;
                                    return newErrors;
                                  });
                                } else {
                                  setInputs({
                                    ...inputs,
                                    [input.key]: isSelected,
                                  });
                                }
                              } else {
                                setInputs({
                                  ...inputs,
                                  [input.key]: isSelected,
                                });
                              }
                            }}
                            size="sm"
                          >
                            <div className="pl-1 font-medium text-sm">
                              {input.label}
                            </div>
                          </Switch>
                          {input.key === "requestVendor" && (
                            <ZTooltip content="Your assessment will automatically proceed once the vendor submits the details." placement="top" showArrow={true} className="w-[300px]">
                              <div className="flex items-center ml-2 text-gray-500">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M12 16V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M12 8H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </div>
                            </ZTooltip>
                          )}
                        </div>
                      </div>
                    );
                  }
                  
                  return (
                    <ZInput
                      key={input.key}
                      label={input.label}
                      placeholder={input.placeholder}
                      type={input.type}
                      value={
                        inputs[input.key] &&
                          typeof inputs[input.key] === "string"
                          ? (inputs[input.key] as string)
                          : ""
                      }
                      onChange={(e) => handleInputChange(e, input.key, input.type)}
                      labelPlacement="outside"
                      maxLength={getCharacterLimit(type, input.key)}
                      errorMessage={errors[input.key]}
                      isInvalid={!!errors[input.key]}
                      variant="faded"
                      className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm shadow-none rounded-md"
                      classNames={{
                        inputWrapper: cn(
                          "w-full shadow-none rounded-md border-1 border-gray-300",
                          !!errors[input.key] && "border-[#F31260]"
                        ),
                      }}
                    />
                  );
                }
              )}
              
              {/* Add vendorEmail input field if requestVendor is selected */}
              {inputs.requestVendor === true && (
                <ZInput
                  key="vendorEmail"
                  label="Vendor Email"
                  placeholder="vendor@company.com"
                  type="email"
                  value={(inputs.vendorEmail as string) || ""}
                  onChange={(e) => handleInputChange(e, "vendorEmail", "email")}
                  labelPlacement="outside"
                  errorMessage={errors.vendorEmail}
                  isInvalid={!!errors.vendorEmail}
                  variant="faded"
                  className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm shadow-none rounded-md"
                  classNames={{
                    inputWrapper: cn(
                      "w-full shadow-none rounded-md border-1 border-gray-300",
                      !!errors.vendorEmail && "border-[#F31260]"
                    ),
                  }}
                />
              )}
              
            </div>
            
            {/* Regular dropdown for non-policy management types */}
            {helperData?.dropdown && type !== AGENT_TYPES.POLICY_MANAGEMENT ? (
              <Select
                classNames={{
                  mainWrapper: cn(
                    "w-full shadow-none rounded-md border-1 border-gray-300",
                    !!errors.session && "border-[#F31260]"
                  ),
                  popoverContent: cn(
                    "w-full shadow-none rounded-md border-1 border-gray-300",
                    !!errors.session && "border-[#F31260]"
                  ),
                }}
                items={filteredDropdownOptions}
                label={helperData.dropdown.label}
                labelPlacement="outside"
                value={selectedDropdownOption}
                placeholder={helperData.dropdown.placeholder}
                onChange={(value) => {
                  setSelectedDropdownOption(value.target.value);
                }}
                selectedKeys={selectedDropdownOption ? new Set([selectedDropdownOption]) : new Set()}
                disabledKeys={selectedDropdownOption ? new Set([selectedDropdownOption]) : new Set()}
              >
                {(item: { value: string; label: string }) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                )}
              </Select>
            ) : null}
            
            {helperData &&
              type === AGENT_TYPES.GAP_ASSESSMENT &&
              (helperData as any)?.toggle && (
                <><div className="pt-4 pb-2">
                  <Switch
                    isSelected={isToggleSelected}
                    onValueChange={setIsToggleSelected}
                    size="sm"
                  >
                    <div className="pl-1 font-medium text-sm">
                      {(helperData as any)?.toggle?.label}
                    </div>
                  </Switch>
                </div>
                <hr />
                </>
              )}
            {selectedDropdownOption === RiskAssessmentTypes.FAIR && (
              <div className="grid w-full items-center gap-1.5">
                <Textarea
                  label="Risk to be evaluated"
                  placeholder="Add risk"
                  value={inputs.risk_statement as string || ""}
                  onChange={(e) => {
                    setErrors({ ...errors, risk: "" });
                    setInputs({
                      ...inputs,
                      risk_statement: e.target.value.trimStart(),
                    });
                  }}
                  labelPlacement="outside"
                  errorMessage={errors.risk}
                  isInvalid={!!errors.risk}
                  variant="faded"
                  classNames={{
                    inputWrapper: cn(
                      "w-full shadow-none rounded-md border-1 border-gray-300",
                      !!errors.risk && "border-[#F31260]"
                    ),
                  }}
                />
              </div>
            )}
            {/* Add questionnaire file upload if requestVendor is selected */}
            {inputs.requestVendor === true && (
              <div className="grid w-full items-center gap-1.5">
                <p className="text-small">Attach Questionnaire (Optional)</p>
                <FileUpload
                  maxFiles={1}
                  isMultiple={true}
                  allowedFileTypes={[
                    "text/csv",
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    "application/vnd.ms-excel"
                  ]}
                  setFiles={(files: UploadedFile[], type: string) =>
                    handleSetFiles(files, type)
                  }
                  type="questionnaire"
                  maxSizeMB={5}
                  ref={(el: HTMLDivElement) =>
                    (fileUploadRefs.current.questionnaire = el)
                  }
                />
              </div>
            )}
            {helperData?.radio && selectedDropdownOption === RiskAssessmentTypes.NIST_CSF_2 && (
              <div className="w-full">
                <p className="text-sm font-medium text-gray-700 mb-1">{helperData.radio.label}</p>
                <RadioGroup
                  orientation="horizontal"
                  value={inputs[helperData.radio.key] as string || helperData.radio.options.find((opt: { value: string; label: string; isDefault?: boolean }) => opt.isDefault)?.value || ""}
                  onValueChange={(value) => {
                    setInputs({
                      ...inputs,
                      [helperData.radio.key]: value,
                    });
                  }}
                  size="sm"
                  isDisabled={false}
                  isInvalid={!!errors.session}
                  errorMessage={errors.session}
                >
                  {helperData.radio.options.map((option: { value: string; label: string; isDefault?: boolean }) => (
                    <Radio
                      key={option.value}
                      value={option.value}
                    >
                      {option.label}
                    </Radio>
                  ))}
                </RadioGroup>
              </div>
            )}
            
            {(helperData?.uploads &&
              // Only show upload if:
              // 1. NOT NIST_CSF_2 framework OR 
              // 2. NIST_CSF_2 framework with "all" radio option selected
              (selectedDropdownOption !== RiskAssessmentTypes.NIST_CSF_2 ||
                inputs[helperData.radio?.key] === "all")
            ) && helperData?.uploads?.map?.(
              ({
                title,
                allowedFileTypes,
                type,
                maxFiles,
                sec_ctx
              }: {
                title: string;
                allowedFileTypes: string[];
                type: string;
                maxFiles: number;
                sec_ctx: string;
              }) => (
                <div className="grid w-full items-center gap-1.5" key={title+selectedDropdownOption}>
                  <p className="text-small">
                    {selectedDropdownOption === RiskAssessmentTypes.FAIR
                      ? "Upload Documents (Optional)"
                      : title}
                  </p>
                  <FileUpload
                    sec_ctx={sec_ctx===AGENT_TYPES.RISK_ASSESSMENT?selectedDropdownOption : sec_ctx}
                    maxFiles={selectedDropdownOption === RiskAssessmentTypes.FAIR ? 5 : selectedDropdownOption === RiskAssessmentTypes.NIST_CSF_2 ? 400 : maxFiles}
                    key={title+selectedDropdownOption}
                    isMultiple={true}
                    allowedFileTypes={allowedFileTypes}
                    setFiles={(files: UploadedFile[], type: string) =>
                      handleSetFiles(files, type)
                    }
                    type={type}
                    ref={(el: HTMLDivElement) =>
                      (fileUploadRefs.current[type] = el)
                    }
                  />
                </div>
              )
            )}
            {inputs.requestVendor === true && (
                <ZInput
                  key="contactEmail"
                  label={
                    <div className="flex items-center">
                      Contact Email
                      <ZTooltip content="Vendors will contact you at this email if they have questions during submission." placement="top" showArrow={true} className="w-[300px]">
                        <div className="flex items-center ml-2 text-gray-500">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M12 16V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M12 8H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                      </ZTooltip>
                    </div>
                  }
                  placeholder="contact@company.com"
                  type="email"
                  value={(inputs.contactEmail as string) || ""}
                  onChange={(e) => handleInputChange(e, "contactEmail", "email")}
                  labelPlacement="outside"
                  errorMessage={errors.contactEmail}
                  isInvalid={!!errors.contactEmail}
                  variant="faded"
                  className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm shadow-none rounded-md"
                  classNames={{
                    inputWrapper: cn(
                      "w-full shadow-none rounded-md border-1 border-gray-300",
                      !!errors.contactEmail && "border-[#F31260]"
                    ),
                  }}
                  
                />
              )}
          </div>
        </ZModalBody>
        <ZModalFooter>
          <ZButton
            variant="bordered"
            onClick={() =>
              setIsOpen({
                isOpen: false,
              })
            }
            className="px-3 py-1 h-auto text-sm"
            isDisabled={isLoading}
          >
            Cancel
          </ZButton>
          <ZButton
            type="submit"
            onClick={() => {
              handleSubmit().catch(e => {
                console.error(e);
                return;
              });
            }}
            isLoading={isLoading}
            isDisabled={isLoading || buttonDisabled}
            disabled={isLoading || buttonDisabled}
            className="px-3 py-1 h-auto text-sm"
          >
            Submit
          </ZButton>
        </ZModalFooter>
      </ZModalContent>
    </ZModal>
  );
}
